import React from 'react';
import _first from 'lodash-es/first';
import { flexRender, Row } from '@tanstack/react-table';
import tw from 'twin.macro';
import { CenterRotatedCell } from 'components/CenterRotatedCell';

interface Props {
  rows: Row<any>[];
  parentRow?: Row<any>;
  nbOfChildrenRowsByGroupCol?: Record<string, number>;
  leafRowsByGroupCol?: Record<string, any>;
  groups: string[];
  depth?: number;
  className?: string;
  isRowUneven?: boolean;
}

function computeNbRows(row: Row<any>) {
  if (row.subRows.length === 0) {
    return 1;
  }
  return row.subRows.reduce((acc, subRow) => acc + computeNbRows(subRow), 0);
}

function getIsCellFromLastGroup(
  colKey: string,
  original: any,
  groups: string[],
) {
  const filteredGroups = groups.filter(g => Object.keys(original).includes(g));
  const lastGroup = _first(filteredGroups.slice().reverse());
  return colKey === lastGroup;
}

// Recursive component that renders the rows
// It goes through all the rows and subrows
// We skip rows that are aggregated since we want only to render leafs rows but with rowspan if aggregated
// To understand this component, you need to check the data model behind rows (it contains subRows)
// and you need to understand how html table works with rowspan and colspan
// this component could be more generic but since I don't need that for now, I didn't do it
// But it might be useful for doing more generic livret, like in college or lycee or make custom one
export function Rows(props: Props) {
  const {
    rows,
    nbOfChildrenRowsByGroupCol = {},
    leafRowsByGroupCol = {},
    groups,
    depth = 0,
  } = props;
  return (
    <>
      {rows.map((row, index) => {
        if ((row?.subRows || []).length > 0) {
          return (
            <Rows
              key={row.id}
              isRowUneven={
                props.isRowUneven || (depth === 0 && index % 2 === 1)
              }
              className={props.className}
              rows={row.subRows}
              nbOfChildrenRowsByGroupCol={{
                ...nbOfChildrenRowsByGroupCol,
                [row.groupingColumnId || '']: computeNbRows(row),
              }}
              groups={groups}
              leafRowsByGroupCol={{
                ...leafRowsByGroupCol,
                [row.groupingColumnId || '']: row
                  .getLeafRows()
                  .filter(r => (r?.getLeafRows() || []).length === 0),
              }}
              depth={row.groupingValue === 'undefined' ? depth : depth + 1}
            />
          );
        }
        return (
          <tr
            className={props.className}
            css={[props.isRowUneven && tw`bg-neutral-supershy`, tw`relative`]}
            key={row.id}
          >
            {row.getVisibleCells().map(cell => {
              const isPlaceholder = cell.getIsPlaceholder();
              const meta = cell?.column?.columnDef?.meta as any;
              const aggregateBy = meta?.aggregateBy;
              const isAggregated = !!aggregateBy;
              let colAggregated = cell?.column?.id;
              if (aggregateBy) {
                colAggregated = aggregateBy;
              }

              const firstLeaf = _first<Row<any>>(
                leafRowsByGroupCol[colAggregated] || [],
              );
              const isFirstLeaf = firstLeaf?.id === cell.row.id;
              const isPlaceholderOrAggregated = isPlaceholder || isAggregated;

              if (isPlaceholderOrAggregated && !isFirstLeaf) {
                return null;
              }
              const isCellFromLastGroup = getIsCellFromLastGroup(
                cell?.column?.id,
                cell?.row?.original,
                groups,
              );
              const doesExistInObject = cell?.row?.original[cell?.column?.id];
              if (isPlaceholder && !doesExistInObject) {
                return null;
              }
              const rowSpan =
                isPlaceholderOrAggregated && isFirstLeaf
                  ? nbOfChildrenRowsByGroupCol[colAggregated]
                  : 1;
              return (
                <td
                  key={cell.id}
                  tw="border px-2 py-4 relative bg-clip-padding"
                  css={[
                    meta?.verticalAlign === 'top' && tw`align-top`,
                    meta?.condensed && tw`py-1 px-1`,
                    rowSpan <= 5 && tw`break-inside-avoid`, // try not to be too aggressive on page breaks, otherwise you end up with a mostly blank page and content on the next page
                  ]}
                  colSpan={
                    isPlaceholder && isCellFromLastGroup
                      ? 1 + groups.length - depth
                      : 1
                  }
                  rowSpan={rowSpan}
                >
                  <CenterRotatedCell
                    active={!isCellFromLastGroup && isPlaceholder}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </CenterRotatedCell>
                </td>
              );
            })}
          </tr>
        );
      })}
    </>
  );
}
